import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SkeletonRow } from '../ui/Skeleton'
import { useShoppingCopy } from '../../hooks/useShoppingCopy'
import { useShopCategoryStyles } from '../../hooks/useShopCategoryStyles'
import {
  aggregateGroceryManifest,
  groupGroceryManifest,
  type AggregatedGroceryItem,
} from '../../lib/aggregateGroceryList'
import { useCartPrices, type CartLineItem, type OptimizedStoreCart } from '../../lib/useCartPrices'
import { toggleGrocerySecured } from '../../lib/grocery'
import { MACRO_BUCKET_ORDER } from '../../lib/ingredientRegistry'
import { materialIconGlyph } from '../../lib/materialIcons'
import { chainToStoreSlug, formatMoney, STORE_DISPLAY_NAMES, type QualityPreference } from '../../lib/groceryPrices'
import type { GroceryItem } from '../../lib/protocolTypes'
import type { NearbyStore } from '../../lib/storeTypes'
import IngredientNutritionSheet from './IngredientNutritionSheet'

type SupplyChainBudgetCardProps = {
  items: GroceryItem[]
  nearbyStores?: NearbyStore[]
  isPremium?: boolean
  preferredCurrency?: string
  currencySymbol?: string
  countryCode?: string
  currencyCode?: string
  onItemUpdated?: () => void
}

function ShopIcon({ name, className = '' }: { name: string; className?: string }) {
  return (
    <span className={`material-symbols-outlined ${className}`} aria-hidden>
      {materialIconGlyph(name)}
    </span>
  )
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduced(mq.matches)
    const onChange = () => setReduced(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return reduced
}

type ActiveStoreSummary = {
  name: string
  total: number
  matched: number
  estimated: number
  itemsSecured: number
  itemsTotal: number
  itemsMissing: number
}

const SupplyChainBudgetCard = ({
  items,
  nearbyStores = [],
  isPremium = false,
  preferredCurrency = 'EUR',
  currencySymbol,
  countryCode = 'AT',
  currencyCode = 'EUR',
  onItemUpdated,
}: SupplyChainBudgetCardProps) => {
  const { t } = useTranslation()
  const { macroBucketLabel, qualityOptions } = useShoppingCopy()
  const categoryStyles = useShopCategoryStyles()
  const reducedMotion = usePrefersReducedMotion()
  const resolvedCurrencyCode = currencyCode ?? preferredCurrency ?? 'EUR'

  const money = (value: number) => {
    if (value <= 0) return '€0.00'
    return formatMoney(value, resolvedCurrencyCode, countryCode, currencySymbol)
  }

  const [pending, setPending] = useState<string | null>(null)
  const [toggleError, setToggleError] = useState('')
  const [qualityPref, setQualityPref] = useState<QualityPreference>('mix')
  const [selectedStore, setSelectedStore] = useState<string | null>(null)
  const [nutritionItem, setNutritionItem] = useState<AggregatedGroceryItem | null>(null)
  const [nutritionProductName, setNutritionProductName] = useState<string | null>(null)

  const aggregatedItems = useMemo(() => aggregateGroceryManifest(items), [items])
  const groupedItems = useMemo(() => groupGroceryManifest(items), [items])

  const { carts: rawCarts, loading: pricingLoading, error: pricingError } = useCartPrices(
    aggregatedItems,
    qualityPref,
    isPremium,
    nearbyStores,
    countryCode,
    resolvedCurrencyCode,
  )

  const carts: OptimizedStoreCart[] = useMemo(() => {
    return rawCarts.map((cart) => {
      const nearbyMatch = nearbyStores.find((s) => chainToStoreSlug(s.chain) === cart.store)
      return {
        ...cart,
        store_display: STORE_DISPLAY_NAMES[cart.store] ?? nearbyMatch?.name ?? cart.store.toUpperCase(),
        distance_km: nearbyMatch?.distance_km ?? cart.distance_km,
      }
    })
  }, [rawCarts, nearbyStores])

  useEffect(() => {
    if (carts.length > 0) {
      setSelectedStore((prev) => {
        if (prev && carts.some((c) => c.store === prev)) return prev
        return carts[0].store
      })
    }
  }, [carts])

  const itemsSecured = aggregatedItems.filter((i) => i.is_secured).length
  const itemsTotal = aggregatedItems.length

  const activeCart = useMemo(
    () => carts.find((c) => c.store === selectedStore) ?? carts[0] ?? null,
    [carts, selectedStore],
  )

  const activeStore: ActiveStoreSummary = useMemo(() => {
    if (isPremium && activeCart) {
      return {
        name: activeCart.store_display,
        total: activeCart.total_cost,
        matched: activeCart.matched_total,
        estimated: activeCart.estimated_total,
        itemsSecured,
        itemsTotal,
        itemsMissing: activeCart.missing_items_count,
      }
    }
    const total = aggregatedItems.reduce((s, i) => s + i.approx_cost, 0)
    const estimated = aggregatedItems.filter((i) => i.is_estimated).reduce((s, i) => s + i.approx_cost, 0)
    return {
      name: t('shopping.budgetEnvelope'),
      total,
      matched: Math.max(0, total - estimated),
      estimated,
      itemsSecured,
      itemsTotal,
      itemsMissing: 0,
    }
  }, [activeCart, aggregatedItems, isPremium, itemsSecured, itemsTotal, t])

  const cartLineByIngredient = useMemo(() => {
    const map = new Map<string, CartLineItem>()
    for (const line of activeCart?.items ?? []) {
      map.set(line.ingredient.toLowerCase(), line)
    }
    return map
  }, [activeCart])

  const securedPct =
    activeStore.itemsTotal > 0
      ? (activeStore.itemsSecured / activeStore.itemsTotal) * 100
      : 0

  const handleToggle = async (item: AggregatedGroceryItem) => {
    setPending(item.id)
    setToggleError('')
    const next = !item.is_secured
    for (const sourceId of item.source_ids) {
      const { error } = await toggleGrocerySecured(sourceId, next)
      if (error) {
        setToggleError(error)
        setPending(null)
        return
      }
    }
    setPending(null)
    onItemUpdated?.()
  }

  const itemPrice = (manifest: AggregatedGroceryItem): number => {
    const line = cartLineByIngredient.get(manifest.canonicalName.toLowerCase())
    if (line && line.line_total > 0) return line.line_total
    return manifest.approx_cost
  }

  const itemQuantity = (manifest: AggregatedGroceryItem): string =>
    manifest.shopping_pack?.label ?? manifest.quantity_mass

  const openNutrition = (item: AggregatedGroceryItem, productName?: string | null) => {
    setNutritionProductName(productName ?? null)
    setNutritionItem(item)
  }

  if (items.length === 0) {
    return (
      <div className="shop-screen flex min-h-[50vh] flex-col items-center justify-center px-6 pb-24 text-center">
        <ShopIcon name="shopping_cart" className="text-[32px] text-[var(--text-3)]" />
        <p className="mt-3 text-[15px] font-semibold text-[var(--text-1)]">{t('shopping.pageTitle')}</p>
        <p className="mt-2 text-[14px] text-[var(--text-2)]">{t('shopping.emptyBody')}</p>
      </div>
    )
  }

  const progressTransition = reducedMotion ? 'none' : 'width 0.5s ease'

  return (
    <div className="shop-screen pb-24">
      <div className="px-6 pt-14">
        <h1 className="text-[28px] font-bold tracking-[-0.8px] text-[var(--text-1)]">
          {t('shopping.pageTitle')}
        </h1>
        <p className="mt-1 text-[14px] tracking-tight text-[var(--text-3)]">
          {isPremium ? t('shopping.pageSubtitlePremium') : t('shopping.pageSubtitleBasic')}
        </p>
      </div>

      {isPremium ? (
        <>
          <div className="mt-4 flex gap-1.5 px-6">
            {qualityOptions.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setQualityPref(opt.id)}
                className={`shop-motion flex-1 rounded-[10px] border py-[9px] text-[14px] font-medium tracking-tight transition-all duration-150 ${
                  qualityPref === opt.id
                    ? 'border-[var(--green)] bg-[var(--green)] font-semibold text-black'
                    : 'border-[var(--border)] bg-[var(--surface)] text-[var(--text-2)]'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {pricingLoading ? (
            <div className="space-y-2 px-6 pt-7" role="status" aria-label={t('shopping.syncingPrices')}>
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
            </div>
          ) : carts.length === 0 ? (
            <p className="mx-6 mt-7 rounded-[16px] border border-[var(--border)] bg-[var(--surface)] p-4 text-[14px] text-[var(--text-2)]">
              {pricingError ? pricingError : t('shopping.noPriceData')}
            </p>
          ) : (
            <>
              <div className="px-6 pb-2.5 pt-7 text-[11px] font-semibold uppercase tracking-[0.6px] text-[var(--text-3)]">
                {t('shopping.compareStores')}
              </div>
              <div className="scrollbar-hide flex gap-2.5 overflow-x-auto px-6">
                {carts.map((cart) => {
                  const isBestValue = cart.is_best_value
                  const isActive = cart.store === selectedStore
                  const fillPct =
                    itemsTotal > 0 ? (cart.items_found_count / itemsTotal) * 100 : 0

                  return (
                    <button
                      key={cart.store}
                      type="button"
                      onClick={() => setSelectedStore(cart.store)}
                      className={`shop-motion relative w-[248px] flex-shrink-0 rounded-[16px] border p-4 text-left transition-all duration-150 ${
                        isBestValue
                          ? 'border-[rgba(0,200,83,0.35)] bg-[var(--surface)]'
                          : isActive
                            ? 'border-[var(--green)] bg-[var(--surface)]'
                            : 'border-[var(--border)] bg-[var(--surface)]'
                      }`}
                      style={
                        isBestValue
                          ? {
                              backgroundImage:
                                'linear-gradient(180deg, rgba(0,200,83,0.08), var(--surface) 60%)',
                            }
                          : undefined
                      }
                    >
                      {isBestValue ? (
                        <div className="absolute -top-px right-4 rounded-b-[8px] bg-[var(--green)] px-2.5 py-1 text-[10px] font-bold tracking-[0.3px] text-black">
                          {t('shopping.bestValueBadge')}
                        </div>
                      ) : null}
                      <div className="mb-2 text-[17px] font-bold tracking-tight text-[var(--text-1)]">
                        {cart.store_display}
                      </div>
                      <div className="mb-1 text-[26px] font-bold tracking-[-1px] text-[var(--green-text)]">
                        {money(cart.total_cost)}
                      </div>
                      <div className="mb-3 text-[12px] tracking-tight text-[var(--text-3)]">
                        {money(cart.matched_total)} {t('shopping.confirmed')} · {money(cart.estimated_total)}{' '}
                        {t('shopping.estShort')}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[12px] font-medium text-[var(--text-2)]">
                          {t('shopping.itemsOfTotal', {
                            found: cart.items_found_count,
                            total: itemsTotal,
                          })}
                        </span>
                        {cart.distance_km != null ? (
                          <span className="flex items-center gap-1 text-[12px] text-[var(--text-3)]">
                            <ShopIcon name="near_me" className="text-[13px]" />
                            {cart.distance_km}km
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-2.5 h-[3px] overflow-hidden rounded-full bg-[var(--surface-2)]">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${fillPct}%`,
                            background: isBestValue ? 'var(--green)' : 'var(--blue)',
                            transition: progressTransition,
                          }}
                        />
                      </div>
                    </button>
                  )
                })}
              </div>
            </>
          )}
        </>
      ) : null}

      <div className="mx-6 mt-4 rounded-[16px] border border-[var(--border)] bg-[var(--surface)] p-5">
        <div className="mb-3.5 flex items-start justify-between">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-[var(--green-d)]">
              <ShopIcon name="storefront" className="text-[18px] text-[var(--green-text)]" />
            </div>
            <div>
              <div className="text-[15px] font-semibold tracking-tight text-[var(--text-1)]">
                {activeStore.name}
              </div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.2px] text-[var(--text-3)]">
                {t('shopping.activeCart')}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[22px] font-bold tracking-[-0.6px] text-[var(--text-1)]">
              {money(activeStore.total)}
            </div>
            <div className="mt-0.5 text-[11px] tracking-tight text-[var(--text-3)]">
              {money(activeStore.matched)} {t('shopping.confirmed')} · {money(activeStore.estimated)}{' '}
              {t('shopping.estShort')}
            </div>
          </div>
        </div>

        <div className="mb-2.5 h-2 overflow-hidden rounded-full bg-[var(--surface-2)]">
          <div
            className="h-full rounded-full bg-[var(--green)]"
            style={{
              width: `${securedPct}%`,
              boxShadow: '0 0 8px rgba(0,200,83,0.35)',
              transition: progressTransition,
            }}
          />
        </div>

        <div className="flex gap-4">
          <div className="flex items-center gap-1.5">
            <div className="h-[7px] w-[7px] rounded-full bg-[var(--green)]" />
            <span className="text-[12px] tracking-tight text-[var(--text-2)]">
              <strong className="font-semibold text-[var(--text-1)]">{activeStore.itemsSecured}</strong>{' '}
              {t('shopping.secured')}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="h-[7px] w-[7px] rounded-full border border-[var(--border)] bg-[var(--surface-2)]" />
            <span className="text-[12px] tracking-tight text-[var(--text-2)]">
              <strong className="font-semibold text-[var(--text-1)]">
                {activeStore.itemsTotal - activeStore.itemsSecured}
              </strong>{' '}
              {t('shopping.remaining')}
            </span>
          </div>
        </div>
      </div>

      {isPremium && activeStore.itemsMissing > 0 ? (
        <div className="mx-6 mt-2.5 flex items-center gap-2.5 rounded-[12px] border border-[rgba(184,120,10,0.18)] bg-[var(--amber-d)] px-4 py-3">
          <ShopIcon name="info_outline" className="text-[16px] text-[var(--amber)]" />
          <span className="flex-1 text-[13px] font-medium tracking-tight text-[var(--amber)]">
            {t('shopping.missingItemsInfo', {
              count: activeStore.itemsMissing,
              store: activeStore.name,
            })}
          </span>
          <button type="button" className="text-[13px] text-[var(--amber)] opacity-70">
            {t('shopping.viewMissing')}
          </button>
        </div>
      ) : null}

      {toggleError ? (
        <p className="mx-6 mt-2 text-[14px] text-[var(--pink)]">{toggleError}</p>
      ) : null}

      {!isPremium ? (
        <p className="mx-6 mt-4 text-[13px] text-[var(--text-3)]">{t('shopping.upgradeHint')}</p>
      ) : null}

      {MACRO_BUCKET_ORDER.map((bucket) => {
        const sectionItems = groupedItems[bucket]
        if (sectionItems.length === 0) return null

        const style = categoryStyles[bucket]
        const label = macroBucketLabel(bucket)

        return (
          <div key={bucket} className="mx-6 mt-[18px]">
            <div className="mb-2.5 flex items-center gap-2">
              <span
                className="rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.4px]"
                style={{
                  background: style.tagBg,
                  color: style.tagColor,
                }}
              >
                {label}
              </span>
              <span className="text-[12px] tracking-tight text-[var(--text-3)]">
                {t('shopping.categoryItemCount', { count: sectionItems.length })}
              </span>
            </div>

            {sectionItems.map((manifest) => {
              const line = cartLineByIngredient.get(manifest.canonicalName.toLowerCase())
              const price = itemPrice(manifest)
              const quantity = itemQuantity(manifest)
              const isPending = pending === manifest.id

              return (
                <div
                  key={manifest.canonicalName}
                  className="mb-1.5 flex items-center gap-3 rounded-[12px] border border-[var(--border)] bg-[var(--surface)] px-3.5 py-3"
                >
                  <button
                    type="button"
                    aria-label={t('shopping.markSecured', { title: manifest.canonicalName })}
                    disabled={isPending}
                    onClick={() => void handleToggle(manifest)}
                    className={`flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-[7px] border-[1.5px] disabled:opacity-50 ${
                      manifest.is_secured
                        ? 'border-[var(--green)] bg-[var(--green)]'
                        : 'border-[var(--border)]'
                    }`}
                  >
                    {manifest.is_secured ? (
                      <ShopIcon name="check" className="text-[14px] text-black" />
                    ) : null}
                  </button>

                  <button
                    type="button"
                    onClick={() => openNutrition(manifest, line?.product_name)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <div
                      className={`text-[14px] font-medium tracking-tight ${
                        manifest.is_secured
                          ? 'text-[var(--text-3)] line-through'
                          : 'text-[var(--text-1)]'
                      }`}
                    >
                      {manifest.canonicalName}
                    </div>
                    <div className="mt-0.5 text-[12px] text-[var(--text-3)]">{quantity}</div>
                  </button>

                  <div className="flex-shrink-0 text-[14px] font-semibold text-[var(--text-2)]">
                    {price > 0 ? money(price) : '—'}
                  </div>
                </div>
              )
            })}
          </div>
        )
      })}

      <IngredientNutritionSheet
        open={nutritionItem != null}
        onClose={() => {
          setNutritionItem(null)
          setNutritionProductName(null)
        }}
        item={nutritionItem}
        productName={nutritionProductName}
      />
    </div>
  )
}

export default SupplyChainBudgetCard
